import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Box, Tooltip, IconButton } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router';
import { get, isEmpty, isArray } from 'lodash';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import { findOrder } from 'modules/Orders/_api';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { encryptId } from 'utils/hooks/useApp';
import { settingHasValue } from 'utils/products';
import { joinParams } from 'utils/study';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';

interface PatientOrdersGridProps {
  birthNumber: string;
  patientId?: string;
  updatedAt?: string;
}

const muiGridKey = 'patientOrdersMUI';

const PatientOrdersGrid: React.FC<PatientOrdersGridProps> = ({
  birthNumber,
  patientId,
  updatedAt,
}) => {
  const { t } = useTranslation(['Orders', 'Grid']);
  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert } = useAlerts();
  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);
  const { portalProductSettings } = useAppInfo();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);

  const handleRowClick = (params: GridRowParams) => {
    const encryptedPatientId = encryptId(get(params.row, 'patient.identificationNumber', ''));
    const encryptedAccessionNumber = encryptId(get(params.row, 'accessionNumber', ''));
    navigate(
      `/orders/${encryptedPatientId}/${encryptedAccessionNumber}/detail?from=patientDetail${
        patientId ? `&patientId=${patientId}` : ''
      }`,
    );
  };

  const showRequestingPhysician = !isEmpty(portalProductSettings)
    ? settingHasValue(portalProductSettings, 'seeRequestingPhysician', 'true')
    : false;

  useEffect(() => {
    const fetchOrders = async () => {
      if (!birthNumber || birthNumber === '-') return;

      toggleLoader(true);
      try {
        const data = await findOrder({ patientId: birthNumber });
        if (data) {
          const processedRows = (isArray(data) ? data : []).map((order: any) => ({
            ...order,
            patientName: {
              lastName: get(order, 'patient.name.lastName', ''),
              firstName: get(order, 'patient.name.firstName', ''),
            },
            patientBirthDate: get(order, 'patient.birthDate', '') || '',
            patientSex: get(order, 'patient.sex', ''),
          }));
          setRows(processedRows);
        }
      } catch (error) {
        addErrorAlert(t('Orders:loadError'));
      } finally {
        toggleLoader(false);
      }
    };

    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birthNumber, updatedAt]);

  const columns = reorderColumnsByGridSettings(
    injectColumnWidthsIntoColumns([
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip title={t('Grid:orderDetail')}>
              <IconButton size="small" color="primary">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'originalDatetimeRequested',
        headerName: t('Orders:datetime'),
        width: 180,
        type: 'date',
        valueGetter: (value: any) => value && new Date(value),
        renderCell: ({ value }: any) => {
          return <Box>{value ? format(value, 'dd. MM. yyyy HH:mm') : '-'}</Box>;
        },
      },
      {
        field: 'patientName',
        headerName: t('Grid:patientName'),
        width: 200,
        valueGetter: (value: any, row: any) =>
          joinParams([get(row, 'patientName.lastName', ''), get(row, 'patientName.firstName', '')]),
      },
      {
        field: 'patientBirthDate',
        headerName: t('Grid:patientBirthDate'),
        width: 125,
        type: 'date',
        valueGetter: (value: any) => (value ? new Date(value) : null),
        valueFormatter: (value: any) => value && format(value, 'dd. MM. yyyy'),
      },
      {
        field: 'patientSex',
        headerName: t('Grid:patientSex'),
        width: 80,
      },
      {
        field: 'accessionNumber',
        headerName: t('Orders:accessionNumber'),
        width: 150,
      },
      {
        field: 'requestedProcedure',
        headerName: t('Orders:requestedProcedure'),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'unit',
        headerName: t('Orders:unit'),
        width: 150,
        valueGetter: (value: any, row: any) => get(row, 'unit.name', ''),
      },
      {
        field: 'station',
        headerName: t('Orders:station'),
        width: 150,
        valueGetter: (value: any, row: any) => get(row, 'station.aetitle', ''),
      },
      {
        field: 'modality',
        headerName: t('Orders:modality'),
        width: 120,
        valueGetter: (value: any, row: any) => get(row, 'modality.name', ''),
      },
      {
        field: 'priorityTranslated',
        headerName: t('Orders:priority'),
        width: 120,
      },
      {
        field: 'state',
        headerName: t('Orders:state'),
        width: 150,
      },
      ...(showRequestingPhysician
        ? [
            {
              field: 'requestingPhysician',
              headerName: t('Orders:requestingPhysician'),
              width: 200,
              valueGetter: (value: any, row: any) =>
                `${get(row, 'requestingPhysician.name.lastName', '') || ''} ${
                  get(row, 'requestingPhysician.name.firstName', '') || ''
                }`,
            },
          ]
        : []),
    ]),
  );

  return (
    <MuiGrid
      gridKey={muiGridKey}
      rows={rows}
      columns={columns}
      getRowId={(row: any) => row.id || row.uniqId}
      hideFooter={true}
      autoHeight={true}
      initialSortMode={[{ field: 'originalDatetimeRequested', sort: 'desc' }]}
      onRowClick={handleRowClick}
    />
  );
};

export default PatientOrdersGrid;
